import { define, reactive } from '@rehaje/reactive'
import { isInheritProto, noop } from '@rehaje/shared'

import { FatalAssertInstanceError } from '../errors'
import { EventPhase } from '../types'
import { Exception } from './Exception'
import { TypedEvent } from './TypedEvent'

import type { ErrorEventConstructor } from '../events'
import type { Store } from '../stores/Store'
import type {
  ErrorConstructor,
  EventEffects,
  EventListener,
  EventTransact,
  ExtractTypedEventListener,
  TypedEventConstructor,
  TypedEventTypes
} from '../types'
import type { LintError } from '@rehaje/errors'
import type { Callback } from '@rehaje/shared'

/**
 * 事件目标基类，为 Rehaje 所有树节点提供与 W3C 标准一致的事件系统(冒泡，捕获机制)
 */
export abstract class EventTarget extends Exception {
  static __r_current_event_target?: EventTarget

  static __r_event_transact_count = 0

  /**
   * 解决循环依赖，依赖反转
   * events/Error.ts -> factories/createEffectHook.ts -> models/EventTarget.ts -> events/Error.ts
   */
  static FixErrorEvent: ErrorEventConstructor

  static RaiseErrorEvent: ErrorEventConstructor

  __r_target_capture_listeners?: Record<string, EventListener[]>

  __r_target_listeners?: Record<string, EventListener[]>

  @reactive('computed')
  get __r_event_parent(): EventTarget | undefined {
    return this.__r_getEventParent?.()
  }

  @reactive('computed')
  get __r_event_root(): EventTarget {
    return this.__r_event_parent?.__r_event_root ?? this
  }

  @reactive('computed')
  get __r_target_bubbled_queue(): EventTarget[] {
    const parent = this.__r_event_parent
    if (parent) {
      return [this, ...parent.__r_target_bubbled_queue]
    }
    return [this]
  }

  constructor(effects?: EventEffects) {
    super()
    define(this)
    this.__r_runEventEffects(effects)
  }

  __r_dispatchCapture(event: TypedEvent) {
    const target = this.__r_getEventTarget() as EventTarget
    const listeners = target.__r_target_capture_listeners?.[event.type]
    if (!listeners) return
    event.currentTarget = target
    for (const handler of listeners) {
      handler.call(event.target, event)
    }
  }

  __r_dispatchEvent(event: TypedEvent): void {
    const queue = this.__r_target_bubbled_queue
    event.__r_event_phase = EventPhase.NONE
    event.target = this.__r_getEventTarget()
    for (let i = queue.length - 1; i >= 0; i--) {
      const target = queue[i]
      event.__r_event_phase =
        target === this ? EventPhase.AT_TARGET : EventPhase.CAPTURING_PHASE
      target.__r_dispatchCapture(event)
      if (event.__r_halted) break
    }
    event.currentTarget = this
    for (const target of queue) {
      event.__r_event_phase =
        target === this ? EventPhase.AT_TARGET : EventPhase.BUBBLING_PHASE
      target.__r_dispatchTarget(event)
      if (!event.bubbles && event.__r_event_phase === EventPhase.AT_TARGET) break
      if (event.__r_halted) break
    }
    event.__r_event_phase = EventPhase.NONE
    if (EventTarget.__r_event_transact_count > 0 && event.__r_default_prevented) {
      throw event
    }
  }

  __r_dispatchTarget(event: TypedEvent) {
    const target = this.__r_getEventTarget() as EventTarget
    const listeners = target.__r_target_listeners?.[event.type]
    if (!listeners) return
    event.currentTarget = target
    for (const handler of listeners) {
      handler.call(event.target, event)
      if (event.__r_terminated) {
        event.__r_terminated = undefined
        break
      }
    }
  }

  /**
   * 获取父节点
   */
  __r_getEventParent(): any {
    return
  }

  /**
   * 获取事件监听目标
   */
  __r_getEventTarget(): any {
    return this
  }

  __r_getEventType(Event: any): string | undefined {
    if (Event instanceof TypedEvent) return Event.type
    if (typeof Event === 'string') return Event
    if (
      typeof Event === 'function' &&
      isInheritProto(Event.prototype, TypedEvent.prototype)
    )
      return new Event().type
  }

  __r_runEventEffects(effects?: EventEffects) {
    const target = EventTarget.__r_current_event_target
    EventTarget.__r_current_event_target = this as any
    try {
      effects?.(this)
    } finally {
      EventTarget.__r_current_event_target = target
    }
  }

  /**
   * 添加事件监听器(符合 W3C 标准)
   * @param event
   * @param listener
   * @param options
   */
  addEventListener<T extends TypedEventTypes>(
    event?: T,
    listener?: ExtractTypedEventListener<T>,
    options?: AddEventListenerOptions
  ): Callback
  addEventListener<Event extends TypedEvent>(
    event?: Event,
    listener?: EventListener<EventTarget, Event>,
    options?: AddEventListenerOptions
  ): Callback
  addEventListener<Event extends TypedEventConstructor>(
    event?: Event,
    listener?: EventListener<EventTarget, InstanceType<Event>>,
    options?: AddEventListenerOptions
  ): Callback
  addEventListener(
    event?: any,
    listener?: EventListener,
    options: AddEventListenerOptions = {}
  ): Callback {
    const eventType = this.__r_getEventType(event)
    if (eventType === undefined || !listener) return noop
    const target = this.__r_getEventTarget() as EventTarget
    if (options.once) {
      const prevListener = listener
      const self = this
      listener = function (event) {
        prevListener.call(this, event)
        self.removeEventListener(eventType, listener, options)
      }
    }

    target.__r_target_capture_listeners ??= {}
    target.__r_target_listeners ??= {}
    if (options.capture) {
      const listeners = target.__r_target_capture_listeners
      listeners[eventType] ??= []
      if (!listeners[eventType].includes(listener))
        listeners[eventType].push(listener)
      return () => this.removeEventListener(eventType, listener, options)
    }
    const listeners = target.__r_target_listeners
    listeners[eventType] ??= []
    if (!listeners[eventType].includes(listener))
      listeners[eventType].push(listener)
    return () => this.removeEventListener(eventType, listener, options)
  }

  /**
   * 触发事件
   * @example
   * // 简单触发
   * dispatchEvent(new CustomEvent())
   * // 事务触发，可以支持 preventDefault()
   * dispatchEvent((dispatch)=>{
   *   dispatch(new CustomEvent())
   *   // 执行其他逻辑，如果事件内部执行 preventDefault，此处逻辑则会被中断执行
   * })
   * @param event
   */
  dispatchEvent(event: TypedEvent): void
  dispatchEvent(transact: EventTransact): void
  dispatchEvent(arg: EventTransact | TypedEvent): void {
    if (arg instanceof TypedEvent) {
      this.__r_dispatchEvent(arg)
    } else {
      try {
        EventTarget.__r_event_transact_count++
        arg((e) => {
          if (e instanceof TypedEvent) this.__r_dispatchEvent(e)
          else throw new FatalAssertInstanceError('TypedEvent')
        })
      } catch (error) {
        if (error instanceof TypedEvent) {
          return
        }
        throw error
      } finally {
        EventTarget.__r_event_transact_count--
      }
    }
  }

  /**
   * 修复错误
   * @param constructor 检查错误构造器
   * @param cause 错误原因，用于精确匹配修复
   */
  override fix<T extends LintError>(
    constructor: ErrorConstructor<T>,
    cause?: any
  ): T | undefined {
    const error = super.fix(constructor, cause)
    if (error) this.dispatchEvent(new EventTarget.FixErrorEvent(error))
    return error as T | undefined
  }

  abstract getStore(): Store

  override raise(error: Error) {
    if (Exception.__r_catch_dispatch ?? true) {
      this.dispatchEvent(new EventTarget.RaiseErrorEvent(error))
    }
    super.raise(error, this.getStore().options?.production)
  }

  /**
   * 移除事件监听器(符合 W3C 标准)
   * @param event
   * @param listener
   * @param options
   */
  removeEventListener<T extends TypedEventTypes>(
    event?: T,
    listener?: ExtractTypedEventListener<T>,
    options?: AddEventListenerOptions
  ): void
  removeEventListener<Event extends TypedEvent>(
    event?: Event,
    listener?: EventListener<EventTarget, Event>,
    options?: AddEventListenerOptions
  ): void
  removeEventListener<Event extends TypedEventConstructor>(
    event?: Event,
    listener?: EventListener<EventTarget, InstanceType<Event>>,
    options?: AddEventListenerOptions
  ): void
  removeEventListener(
    event?: any,
    listener?: EventListener,
    options: AddEventListenerOptions = {}
  ): void {
    const eventType = this.__r_getEventType(event)
    if (eventType === undefined || !listener) return
    const target = this.__r_getEventTarget() as EventTarget
    if (options.capture) {
      const listeners = target.__r_target_capture_listeners
      if (listeners?.[eventType])
        listeners[eventType] = listeners[eventType].filter(
          (item: any) => item !== listener
        )
    } else {
      const listeners = target.__r_target_listeners
      if (listeners?.[eventType])
        listeners[eventType] = listeners[eventType].filter(
          (item: any) => item !== listener
        )
    }
  }
}
